// Seikkailukortit: rastien leimaus ja edistyminen puhelimessa.
//
// Kortin sisällön (rastit, reitti, tekstit) laatii palvelin (backend/app/adventures.py).
// Täällä ratkaistaan vain, mitkä rastit on jo leimattu, mikä on seuraava ja milloin
// seikkailu on valmis. Kaikki funktiot ovat puhtaita, jotta ne voi testata ilman karttaa.
//
// Leimaus on sallittu missä järjestyksessä tahansa: lapsi saa itse päättää, mihin rastiin
// menee ensin. Kortin reitti on vain ehdotus.

import { distanceM, tileKey } from "./geo.js";

/** Kohderastin oletussäde metreinä, jos palvelin ei kerro sitä. */
export const DEFAULT_POI_RADIUS_M = 40;

/** Ruutuun sijoittuvan rastin zoom-taso, esim. "17/9300/4700" → 17. */
function tileZoom(tile) {
  return Number(String(tile).split("/")[0]);
}

/**
 * Onko sijainti rastilla?
 * - Ruutu-rasti: sijainti on kyseisessä ruudussa (sama ruutu kertyy retkellä joka tapauksessa).
 * - Kohde-rasti: sijainti on kohteen säteen sisällä.
 */
export function stopReached(stop, lat, lon) {
  if (stop.kind === "poi") {
    return distanceM(lat, lon, stop.lat, stop.lon) <= (stop.radius_m ?? DEFAULT_POI_RADIUS_M);
  }
  return tileKey(lat, lon, tileZoom(stop.tile)) === stop.tile;
}

/**
 * Uudet leimaukset sijainnilla. `reached` on jo leimattujen rastien indeksit.
 * Palauttaa { reached, fresh }: päivitetty lista (nousevassa järjestyksessä) ja vasta
 * nyt leimatut indeksit, joista näytetään ilmoitus.
 */
export function punch(stops, reached, lat, lon) {
  const have = new Set(reached);
  const fresh = [];
  stops.forEach((stop, i) => {
    if (!have.has(i) && stopReached(stop, lat, lon)) fresh.push(i);
  });
  return { reached: [...have, ...fresh].sort((a, b) => a - b), fresh };
}

/** Seikkailu on valmis, kun jokainen rasti on leimattu. */
export function isFinished(card, reached) {
  return card.stops.length > 0 && reached.length >= card.stops.length;
}

/**
 * Lähin leimaamaton rasti sijainnista: { index, stop, distance } tai null.
 * Sijainnin puuttuessa palautetaan ensimmäinen leimaamaton ilman etäisyyttä.
 */
export function nextStop(card, reached, pos) {
  const have = new Set(reached);
  let best = null;
  card.stops.forEach((stop, index) => {
    if (have.has(index)) return;
    const distance = pos ? distanceM(pos.lat, pos.lon, stop.lat, stop.lon) : null;
    if (!best || (distance !== null && distance < best.distance)) best = { index, stop, distance };
  });
  return best;
}

/** Etäisyys luettavaksi: "180 m" tai "1,2 km". Pyöristys 10 m:iin alle kilometrillä. */
export function distanceText(m) {
  const rounded = Math.max(10, Math.round(m / 10) * 10);
  if (rounded < 1000) return `${rounded} m`; // 995–999 m pyöristyy tuhanteen ja näytetään kilometreinä
  return `${(m / 1000).toLocaleString("fi-FI", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
}

/** Kävelyaika luettavaksi: "8 min" tai "1 h 5 min". */
export function minutesText(min) {
  const m = Math.max(1, Math.round(min));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}`;
}

/**
 * Kortin pituus. Maastoreitillä (palvelin tuntee tiet, vedet ja metsän) luku on kuljettava matka ja
 * kävelyaika; muuten linnuntietä, koska reittiä ei voitu laskea.
 */
export function lengthText(card) {
  if (card.routing === "maasto" && card.walk_m) return `noin ${distanceText(card.walk_m)}, ${minutesText(card.walk_min)} kävellen`;
  return `noin ${distanceText(card.length_m)} linnuntietä`;
}

/**
 * Selite korttien alle: kertoo, miten matka on laskettu. Palauttaa aina tekstin, koska luvun
 * alkuperä pitää voida tarkistaa. `pending`: palvelin hakee alueen kartta-aineistoa taustalla.
 */
export function routeNote(cards, pending = false) {
  const routed = cards.length > 0 && cards.every((c) => c.routing === "maasto");
  if (routed) {
    return "Reitti on laskettu OpenStreetMapin teistä, poluista ja maastosta: vedet ja rakennukset kierretään, tiellä kävely on nopeinta, metsässä ja suolla hitaampaa. Kartta voi olla puutteellinen, joten tarkista reitti kartalta. Oma jälkesi piirtyy aina sinne, missä kuljet.";
  }
  const base = "Matka on laskettu linnuntietä. Tarkista reitti kartalta ennen lähtöä. Kävellen matka on yleensä noin kolmanneksen pidempi.";
  return pending ? `${base} Alueen tiet ja vesistöt haetaan palvelimelle taustalla: paina hetken kuluttua "Uusi ehdotus", niin reitti lasketaan maastoon.` : base;
}

/** "3 rastia", "1 rasti". */
export function stopsText(n) {
  return n === 1 ? "1 rasti" : `${n} rastia`;
}

/** Kortin reitin rajat Leafletin muodossa [[etelä, länsi], [pohjoinen, itä]]. */
export function routeBounds(card) {
  const lats = card.route.map((p) => p[0]);
  const lons = card.route.map((p) => p[1]);
  return [
    [Math.min(...lats), Math.min(...lons)],
    [Math.max(...lats), Math.max(...lons)],
  ];
}

/**
 * Käynnistää seikkailun kortista. Tunniste luodaan kerran puhelimessa, jotta saman
 * suorituksen kirjaus uudelleen lähetettäessä ei tuplaudu palvelimella.
 */
export function startAdventure(card, { player, companions = [], uuid = () => crypto.randomUUID() }) {
  return { id: uuid(), card, reached: [], player, companions: [...companions] };
}

/**
 * Seikkailun kirjaus palvelimelle: lähetetään kullekin osallistujalle (tallentaja ja kaverit).
 * Palauttaa [{ player, body }].
 */
export function completionPosts(adventure, tripId = null) {
  const { card } = adventure;
  const body = {
    id: adventure.id,
    length: card.length,
    title: card.title,
    stops: card.stops.length,
    reached: adventure.reached.length,
    trip_id: tripId,
  };
  return [adventure.player, ...adventure.companions].map((player) => ({ player, body }));
}
